import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import HolisticLayout from '../screens/HolisticLayout';
import StageSelection from '../stages/StageSelection';
import PreparatorySample from '../stages/preparatory/screens/PreparatorySample';

// Foundation screens
import HolisticDashboard from '../stages/foundation/screens/HolisticDashboard';
import RegionDrillDown from '../stages/foundation/screens/RegionDrillDown';
import SchoolDrillDown from '../stages/foundation/screens/SchoolDrillDown';
import ClassDrillDown from '../stages/foundation/screens/ClassDrillDown';
import EntityListScreen from '../stages/foundation/screens/EntityListScreen';
import StudentList from '../stages/foundation/screens/StudentList';
import StudentProfile from '../stages/foundation/screens/StudentProfile';
import BehaviorAnalysis from '../stages/foundation/screens/BehaviorAnalysis';
import PeerAnalysis from '../stages/foundation/screens/PeerAnalysis';
import ParentAnalysis from '../stages/foundation/screens/ParentAnalysis';
import TeacherAnalysis from '../stages/foundation/screens/TeacherAnalysis';
import PhysicalAnalysis from '../stages/foundation/screens/PhysicalAnalysis';
import InterestsAnalysis from '../stages/foundation/screens/InterestsAnalysis';
import Reports from '../stages/foundation/screens/Reports';

// Foundation tabs
import OverviewTab from '../stages/foundation/tabs/OverviewTab';
import BehaviorTab from '../stages/foundation/tabs/BehaviorTab';
import PeerTab from '../stages/foundation/tabs/PeerTab';
import ParentTab from '../stages/foundation/tabs/ParentTab';
import TeacherTab from '../stages/foundation/tabs/TeacherTab';
import InterestsTab from '../stages/foundation/tabs/InterestsTab';
import PhysicalTab from '../stages/foundation/tabs/PhysicalTab';
import CompetencyTab from '../stages/foundation/tabs/CompetencyTab';

// Preparatory screens
import PrepHolisticDashboard from '../stages/preparatory/screens/HolisticDashboard';
import PrepRegionDrillDown from '../stages/preparatory/screens/RegionDrillDown';
import PrepSchoolDrillDown from '../stages/preparatory/screens/SchoolDrillDown';
import PrepClassDrillDown from '../stages/preparatory/screens/ClassDrillDown';
import PrepEntityListScreen from '../stages/preparatory/screens/EntityListScreen';
import PrepStudentList from '../stages/preparatory/screens/StudentList';
import PrepStudentProfile from '../stages/preparatory/screens/StudentProfile';
import PrepSchoolPerformanceScreen from '../stages/preparatory/screens/SchoolPerformanceScreen';
import FoundationSchoolPerformanceScreen from '../stages/foundation/screens/SchoolPerformanceScreen';
// TODO: Uncomment when non-academic data is available
// import PrepBehaviorAnalysis from '../stages/preparatory/screens/BehaviorAnalysis';
// import PrepPeerAnalysis from '../stages/preparatory/screens/PeerAnalysis';
// import PrepParentAnalysis from '../stages/preparatory/screens/ParentAnalysis';
// import PrepTeacherAnalysis from '../stages/preparatory/screens/TeacherAnalysis';
// import PrepPhysicalAnalysis from '../stages/preparatory/screens/PhysicalAnalysis';
// import PrepInterestsAnalysis from '../stages/preparatory/screens/InterestsAnalysis';
// import PrepReports from '../stages/preparatory/screens/Reports';
import AcademicEvaluation from '../stages/preparatory/screens/AcademicEvaluation';

// Preparatory tabs
import PrepOverviewTab from '../stages/preparatory/tabs/OverviewTab';
// TODO: Uncomment when non-academic data is available
// import PrepBehaviorTab from '../stages/preparatory/tabs/BehaviorTab';
// import PrepPeerTab from '../stages/preparatory/tabs/PeerTab';
// import PrepParentTab from '../stages/preparatory/tabs/ParentTab';
// import PrepTeacherTab from '../stages/preparatory/tabs/TeacherTab';
// import PrepInterestsTab from '../stages/preparatory/tabs/InterestsTab';
// import PrepPhysicalTab from '../stages/preparatory/tabs/PhysicalTab';
import PrepAcademicTab from '../stages/preparatory/tabs/AcademicTab';

// Middle screens
import MiddleHolisticDashboard from '../stages/middle/screens/HolisticDashboard';
import MiddleRegionDrillDown from '../stages/middle/screens/RegionDrillDown';
import MiddleSchoolDrillDown from '../stages/middle/screens/SchoolDrillDown';
import MiddleClassDrillDown from '../stages/middle/screens/ClassDrillDown';
import MiddleEntityListScreen from '../stages/middle/screens/EntityListScreen';
import MiddleStudentList from '../stages/middle/screens/StudentList';
import MiddleStudentProfile from '../stages/middle/screens/StudentProfile';
import MiddleSchoolPerformanceScreen from '../stages/middle/screens/SchoolPerformanceScreen';
import MiddleAcademicEvaluation from '../stages/middle/screens/AcademicEvaluation';

// Middle tabs
import MiddleOverviewTab from '../stages/middle/tabs/OverviewTab';
import MiddleAcademicTab from '../stages/middle/tabs/AcademicTab';

// Secondary screens
import SecHolisticDashboard from '../stages/secondary/screens/HolisticDashboard';
import SecRegionDrillDown from '../stages/secondary/screens/RegionDrillDown';
import SecSchoolDrillDown from '../stages/secondary/screens/SchoolDrillDown';
import SecClassDrillDown from '../stages/secondary/screens/ClassDrillDown';
import SecEntityListScreen from '../stages/secondary/screens/EntityListScreen';
import SecStudentList from '../stages/secondary/screens/StudentList';
import SecStudentProfile from '../stages/secondary/screens/StudentProfile';
import SecSchoolPerformanceScreen from '../stages/secondary/screens/SchoolPerformanceScreen';
import SecAcademicEvaluation from '../stages/secondary/screens/AcademicEvaluation';

// Secondary tabs
import SecOverviewTab from '../stages/secondary/tabs/OverviewTab';
import SecAcademicTab from '../stages/secondary/tabs/AcademicTab';


const StudentHolisticRoutes: React.FC = () => (
  <Routes>
    <Route element={<HolisticLayout />}>
      {/* National Overview */}
      <Route index element={<HolisticDashboard />} />

      {/* Stage selection */}
      <Route path="stages" element={<StageSelection />} />

      {/* ── Preparatory Stage (Class III–V) ── */}
      <Route path="stages/preparatory" element={<PrepHolisticDashboard />} />

      {/* Preparatory entity lists */}
      <Route path="stages/preparatory/list/regions" element={<PrepEntityListScreen entityType="regions" />} />
      <Route path="stages/preparatory/list/regions/:level" element={<PrepEntityListScreen entityType="regions" />} />
      <Route path="stages/preparatory/region/:regionId/list/schools" element={<PrepEntityListScreen entityType="schools" />} />
      <Route path="stages/preparatory/region/:regionId/list/schools/:level" element={<PrepEntityListScreen entityType="schools" />} />
      <Route path="stages/preparatory/region/:regionId/school/:schoolId/list/classes" element={<PrepEntityListScreen entityType="classes" />} />
      <Route path="stages/preparatory/region/:regionId/school/:schoolId/list/classes/:level" element={<PrepEntityListScreen entityType="classes" />} />

      {/* Preparatory drill-down */}
      <Route path="stages/preparatory/region/:regionId" element={<PrepRegionDrillDown />} />
      <Route path="stages/preparatory/region/:regionId/school/:schoolId" element={<PrepSchoolDrillDown />} />
      <Route path="stages/preparatory/region/:regionId/school/:schoolId/class/:classId" element={<PrepClassDrillDown />} />

      {/* Preparatory student profile */}
      <Route path="stages/preparatory/region/:regionId/school/:schoolId/class/:classId/students/:studentId" element={<PrepStudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<PrepOverviewTab />} />
        {/* TODO: Uncomment when non-academic data is available */}
        {/* <Route path="behavior" element={<PrepBehaviorTab />} /> */}
        {/* <Route path="peers" element={<PrepPeerTab />} /> */}
        {/* <Route path="parents" element={<PrepParentTab />} /> */}
        {/* <Route path="teacher" element={<PrepTeacherTab />} /> */}
        {/* <Route path="interests" element={<PrepInterestsTab />} /> */}
        {/* <Route path="physical" element={<PrepPhysicalTab />} /> */}
        <Route path="academic" element={<PrepAcademicTab />} />
      </Route>

      {/* Preparatory school performance overview */}
      <Route path="stages/preparatory/school-performance" element={<PrepSchoolPerformanceScreen />} />

      {/* Preparatory aggregate analysis */}
      <Route path="stages/preparatory/students" element={<PrepStudentList />} />
      {/* TODO: Uncomment when non-academic data is available */}
      {/* <Route path="stages/preparatory/behavior" element={<PrepBehaviorAnalysis />} /> */}
      {/* <Route path="stages/preparatory/peer" element={<PrepPeerAnalysis />} /> */}
      {/* <Route path="stages/preparatory/parent" element={<PrepParentAnalysis />} /> */}
      {/* <Route path="stages/preparatory/teacher" element={<PrepTeacherAnalysis />} /> */}
      {/* <Route path="stages/preparatory/physical" element={<PrepPhysicalAnalysis />} /> */}
      {/* <Route path="stages/preparatory/interests" element={<PrepInterestsAnalysis />} /> */}
      {/* <Route path="stages/preparatory/reports" element={<PrepReports />} /> */}
      <Route path="stages/preparatory/academic" element={<AcademicEvaluation />} />

      {/* Preparatory flat student profile */}
      <Route path="stages/preparatory/student/:studentId" element={<PrepStudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<PrepOverviewTab />} />
        {/* TODO: Uncomment when non-academic data is available */}
        {/* <Route path="behavior" element={<PrepBehaviorTab />} /> */}
        {/* <Route path="peers" element={<PrepPeerTab />} /> */}
        {/* <Route path="parents" element={<PrepParentTab />} /> */}
        {/* <Route path="teacher" element={<PrepTeacherTab />} /> */}
        {/* <Route path="interests" element={<PrepInterestsTab />} /> */}
        {/* <Route path="physical" element={<PrepPhysicalTab />} /> */}
        <Route path="academic" element={<PrepAcademicTab />} />
      </Route>

      {/* ── Middle Stage (Class VI–VIII) ── */}
      <Route path="stages/middle" element={<MiddleHolisticDashboard />} />

      {/* Middle entity lists */}
      <Route path="stages/middle/list/regions" element={<MiddleEntityListScreen entityType="regions" />} />
      <Route path="stages/middle/list/regions/:level" element={<MiddleEntityListScreen entityType="regions" />} />
      <Route path="stages/middle/region/:regionId/list/schools" element={<MiddleEntityListScreen entityType="schools" />} />
      <Route path="stages/middle/region/:regionId/list/schools/:level" element={<MiddleEntityListScreen entityType="schools" />} />
      <Route path="stages/middle/region/:regionId/school/:schoolId/list/classes" element={<MiddleEntityListScreen entityType="classes" />} />
      <Route path="stages/middle/region/:regionId/school/:schoolId/list/classes/:level" element={<MiddleEntityListScreen entityType="classes" />} />

      {/* Middle drill-down */}
      <Route path="stages/middle/region/:regionId" element={<MiddleRegionDrillDown />} />
      <Route path="stages/middle/region/:regionId/school/:schoolId" element={<MiddleSchoolDrillDown />} />
      <Route path="stages/middle/region/:regionId/school/:schoolId/class/:classId" element={<MiddleClassDrillDown />} />

      {/* Middle student profile */}
      <Route path="stages/middle/region/:regionId/school/:schoolId/class/:classId/students/:studentId" element={<MiddleStudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<MiddleOverviewTab />} />
        <Route path="academic" element={<MiddleAcademicTab />} />
      </Route>

      {/* Middle school performance overview */}
      <Route path="stages/middle/school-performance" element={<MiddleSchoolPerformanceScreen />} />

      {/* Middle aggregate analysis */}
      <Route path="stages/middle/students" element={<MiddleStudentList />} />
      <Route path="stages/middle/academic" element={<MiddleAcademicEvaluation />} />

      {/* Middle flat student profile */}
      <Route path="stages/middle/student/:studentId" element={<MiddleStudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<MiddleOverviewTab />} />
        <Route path="academic" element={<MiddleAcademicTab />} />
      </Route>

      {/* ── Secondary Stage (Class IX & XI) ── */}
      <Route path="stages/secondary" element={<SecHolisticDashboard />} />

      {/* Secondary entity lists */}
      <Route path="stages/secondary/list/regions" element={<SecEntityListScreen entityType="regions" />} />
      <Route path="stages/secondary/list/regions/:level" element={<SecEntityListScreen entityType="regions" />} />
      <Route path="stages/secondary/region/:regionId/list/schools" element={<SecEntityListScreen entityType="schools" />} />
      <Route path="stages/secondary/region/:regionId/list/schools/:level" element={<SecEntityListScreen entityType="schools" />} />
      <Route path="stages/secondary/region/:regionId/school/:schoolId/list/classes" element={<SecEntityListScreen entityType="classes" />} />
      <Route path="stages/secondary/region/:regionId/school/:schoolId/list/classes/:level" element={<SecEntityListScreen entityType="classes" />} />

      {/* Secondary drill-down */}
      <Route path="stages/secondary/region/:regionId" element={<SecRegionDrillDown />} />
      <Route path="stages/secondary/region/:regionId/school/:schoolId" element={<SecSchoolDrillDown />} />
      <Route path="stages/secondary/region/:regionId/school/:schoolId/class/:classId" element={<SecClassDrillDown />} />

      {/* Secondary student profile */}
      <Route path="stages/secondary/region/:regionId/school/:schoolId/class/:classId/students/:studentId" element={<SecStudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<SecOverviewTab />} />
        <Route path="academic" element={<SecAcademicTab />} />
      </Route>

      {/* Secondary school performance overview */}
      <Route path="stages/secondary/school-performance" element={<SecSchoolPerformanceScreen />} />

      {/* Secondary aggregate analysis */}
      <Route path="stages/secondary/students" element={<SecStudentList />} />
      <Route path="stages/secondary/academic" element={<SecAcademicEvaluation />} />

      {/* Secondary flat student profile */}
      <Route path="stages/secondary/student/:studentId" element={<SecStudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<SecOverviewTab />} />
        <Route path="academic" element={<SecAcademicTab />} />
      </Route>

      {/* Foundation school performance overview */}
      <Route path="school-performance" element={<FoundationSchoolPerformanceScreen />} />

      {/* Entity List screens (filtered / see-all) */}
      <Route path="list/regions" element={<EntityListScreen entityType="regions" />} />
      <Route path="list/regions/:level" element={<EntityListScreen entityType="regions" />} />
      <Route path="region/:regionId/list/schools" element={<EntityListScreen entityType="schools" />} />
      <Route path="region/:regionId/list/schools/:level" element={<EntityListScreen entityType="schools" />} />
      <Route path="region/:regionId/school/:schoolId/list/classes" element={<EntityListScreen entityType="classes" />} />
      <Route path="region/:regionId/school/:schoolId/list/classes/:level" element={<EntityListScreen entityType="classes" />} />

      {/* Layered Drill-down */}
      <Route path="region/:regionId" element={<RegionDrillDown />} />
      <Route path="region/:regionId/school/:schoolId" element={<SchoolDrillDown />} />
      <Route path="region/:regionId/school/:schoolId/class/:classId" element={<ClassDrillDown />} />

      {/* Student Profile (reachable from class drill-down) */}
      <Route path="region/:regionId/school/:schoolId/class/:classId/students/:studentId" element={<StudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OverviewTab />} />
        <Route path="competency" element={<CompetencyTab />} />
        <Route path="behavior" element={<BehaviorTab />} />
        <Route path="peers" element={<PeerTab />} />
        <Route path="parents" element={<ParentTab />} />
        <Route path="teacher" element={<TeacherTab />} />
        <Route path="interests" element={<InterestsTab />} />
        <Route path="physical" element={<PhysicalTab />} />
      </Route>

      {/* Legacy flat routes (aggregate analysis screens) */}
      <Route path="students" element={<StudentList />} />
      <Route path="behavior" element={<BehaviorAnalysis />} />
      <Route path="peer" element={<PeerAnalysis />} />
      <Route path="parent" element={<ParentAnalysis />} />
      <Route path="teacher" element={<TeacherAnalysis />} />
      <Route path="physical" element={<PhysicalAnalysis />} />
      <Route path="interests" element={<InterestsAnalysis />} />
      <Route path="reports" element={<Reports />} />

      {/* Legacy flat student profile */}
      <Route path="student/:studentId" element={<StudentProfile />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OverviewTab />} />
        <Route path="competency" element={<CompetencyTab />} />
        <Route path="behavior" element={<BehaviorTab />} />
        <Route path="peers" element={<PeerTab />} />
        <Route path="parents" element={<ParentTab />} />
        <Route path="teacher" element={<TeacherTab />} />
        <Route path="interests" element={<InterestsTab />} />
        <Route path="physical" element={<PhysicalTab />} />
      </Route>
    </Route>
  </Routes>
);

export default StudentHolisticRoutes;
